/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import root from 'window-or-global';
import { createItemFulfillmentAvailabilityString } from '@/utils/itemHelpers/fulfillmentHelpers/createItemFulfillmentAvailabilityString';
import { badges } from '@/constants/analytics/badges';
import type {
  ShelfLeftNav,
  ShelfPageBodyComplete,
  ShelfPageBodyItemResultsList,
  ShelfPart,
  SortingOption,
} from '@/types/legacy';
import { type FilterDataType, type FilterSelectionType } from '@/types/filterTypes';
import { type IItem } from '@/components/AZCustomComponent/BreadcrumbSTARC/utils/isBreadcrumbModel';
import { type HeaderData } from '@/features/header';
import { type ParsedUrlQuery } from 'querystring';
import { type MappedBrandShelfResults, type MappedBrandShelfResultsAces } from '@/features/brands';
import { sessionStorage } from '../sessionStorage';
import { determineOptionsToDisplayButtons } from '@/features/fulfillment/api/optionsToDisplay';
import type { FulfillmentGroup } from '@/types/reduxStore/skuDetails';
import { type Locale } from '@/types/i18n';
import { countryCodes } from '@/constants/locale';
import {
  isBestSellerBadgeShown,
  isBonusRewardsBadgeShown,
} from '@/features/shelf/components/PageTemplates/Shelf/ResultsList/utils';

export const displayViews = {
  list: 'List View',
  grid: 'Grid View',
  nullResults: '-Null Results',
  nullResultsCartridge: '-Null Results Cartridge',
} as const;

export const getShelfAnalytics = (currentProps: {
  parts: ShelfPart[] | undefined;
  shelfPageBody?: ShelfPageBodyComplete;
  brandPageBody?: MappedBrandShelfResults | MappedBrandShelfResultsAces;
  shelfCatLabel?: string;
  productFilterData?:
    | {
        '@type': string;
        refinementList: Array<{ label: string }>;
      }
    | {
        mobile: boolean;
        tablet: boolean;
        desktop: boolean;
        '@type': string;
        contents: [
          {
            '@type': string;
            refinementList: Array<{ label: string }>;
          },
        ];
      }
    | null;
  selectedNullCartridge: string;
  getCustomPriceFilterLabel: (
    includeCustomPriceLabel?: boolean,
    minPrice?: string | null,
    maxPrice?: string | null,
  ) => string;
  getFilterAnalytics: (selectedFilter: FilterSelectionType, isChecked: boolean) => string;
  query: ParsedUrlQuery;
  headerData: HeaderData | undefined;
  sessionFiltersList?: FilterSelectionType[];
  makeOrModelName?: string | undefined | null;
  defaultEngine: { value: string; label: string } | string | null | undefined;
  shelfBreadcrumb?: IItem[] | undefined;
  staticNavigation?: FilterDataType[] | undefined;
  shelfLeftNav?: ShelfLeftNav | undefined;
  isList?: boolean;
  dealBadgeEnabled: boolean;
  enableVdpDsUniqueDisplay: boolean;
  acesEnabled?: boolean;
  storeSelected: boolean;
  showStrikeThroughPricing?: boolean;
  locale: Locale;
  isShelfPage: boolean;
  isBonusRewardsBadgeEnabled: boolean;
}): {
  badgesInfo: string[];
  bopusList: boolean[];
  categoryLevel1: string;
  categoryLevel2: string;
  categoryLevel3: string;
  dealFlagList: boolean[];
  dealBundleCountList: string[];
  displayViewType: string;
  fulfillmentAvailabilityList: string[];
  nddEligibleList: boolean[];
  nddEligibleProductsCount: number;
  originalPartTypeIdList?: string[];
  partGroupId?: string[];
  pageDepth: string;
  productBrandList: string[];
  productCorePriceList: string[];
  productFilterSelection: string | undefined;
  productIdList: string[];
  productNotAvailableList: boolean[];
  productPart: string;
  productPrice: string[];
  productRepositoryIdList?: string[];
  partTerminologyId?: string[];
  searchResultCount: string;
  resultsPerPage: string;
  sthList: boolean[];
  sthSpecialOrderList: boolean[];
  clearanceList: string[];
  productRecommendationTypeList: string[];
  sisterStoreAvailableList: string[];
  productPartNumberList: string[];
  productFitmentCheckMessage: string[];
  productDealMessage: string[];
  productFulfillmentOptionSeenByCustomer: string[];
} => {
  const {
    parts,
    shelfPageBody,
    brandPageBody,
    shelfCatLabel,
    productFilterData,
    selectedNullCartridge,
    getCustomPriceFilterLabel,
    getFilterAnalytics,
    sessionFiltersList,
    query,
    acesEnabled,
    isShelfPage,
    isBonusRewardsBadgeEnabled,
  } = currentProps;
  const isUS = currentProps.locale === countryCodes.us;
  const categoryLevel1 = currentProps.shelfBreadcrumb?.[0]?.displayName ?? '';
  const categoryLevel2 = currentProps.shelfBreadcrumb?.[1]?.displayName ?? '';
  const categoryLevel3 = currentProps.shelfBreadcrumb?.[2]?.displayName ?? '';
  const staticNavigations =
    currentProps.shelfLeftNav?.[0]?.staticNavigation ?? currentProps.staticNavigation ?? [];
  const dynamicNavigations =
    currentProps.shelfLeftNav?.[0]?.dynamicNavigation?.attributeNarrowingList ?? [];
  const setFiltersInSessionStorage = (filterAnalytics: string) => {
    sessionStorage.setItem('filterSelectionAnalytics', filterAnalytics);
    if (!filterAnalytics.length) {
      sessionStorage.removeItem('filterSelectionAnalytics');
    }
  };

  const staticAndDynamicNavigations = [...staticNavigations, ...dynamicNavigations];

  sessionFiltersList?.forEach((sessionFilter) => {
    let sessionFilterIsStillSelected = false;
    staticAndDynamicNavigations.forEach((facet) => {
      const foundFilter = facet.refinements.find(
        (refinement) =>
          refinement.label === sessionFilter.value &&
          facet.displayName === sessionFilter.facet &&
          refinement.properties.isSelected,
      );
      sessionFilterIsStillSelected = sessionFilterIsStillSelected ? true : !!foundFilter;
    });

    if (!sessionFilterIsStillSelected) {
      const filterAnalytics = getFilterAnalytics(sessionFilter, sessionFilterIsStillSelected);
      setFiltersInSessionStorage(filterAnalytics);
    }
  });

  staticAndDynamicNavigations.forEach((facet) => {
    facet.refinements.forEach((refinement) => {
      const filterWithType: FilterSelectionType = {
        facet: facet.displayName,
        value: refinement.label,
      };

      const filterAnalytics = getFilterAnalytics(
        filterWithType,
        !!refinement.properties.isSelected,
      );
      setFiltersInSessionStorage(filterAnalytics);
    });
  });

  if (
    (query.minPrice || query.maxPrice) &&
    !Array.isArray(query.minPrice) &&
    !Array.isArray(query.maxPrice)
  ) {
    const customPriceFilter = getCustomPriceFilterLabel(true, query.minPrice, query.maxPrice);
    const filterDataArray = customPriceFilter.split(': ');
    const filterObject: FilterSelectionType = {
      type: filterDataArray[0],
      facet: filterDataArray[1],
      value: filterDataArray[2],
    };

    const analyticsWithCustomPrice = getFilterAnalytics(filterObject, true);
    setFiltersInSessionStorage(analyticsWithCustomPrice);
  } else {
    const customPriceFilter = getCustomPriceFilterLabel(true);
    const filterDataArray = customPriceFilter.split(': ');
    const filterObject: FilterSelectionType = {
      type: filterDataArray[0],
      facet: filterDataArray[1],
      value: filterDataArray[2],
    };

    const analyticsWithCustomPrice = getFilterAnalytics(filterObject, false);
    setFiltersInSessionStorage(analyticsWithCustomPrice);
  }

  const productFilterSelection = sessionStorage.getItem('filterSelectionAnalytics') ?? '';
  const productIdList: string[] = [];
  const productPartNumberList: string[] = [];
  const productBrandList: string[] = [];
  const bopusList: boolean[] = [];
  const sthList: boolean[] = [];
  const nddEligibleList: boolean[] = [];
  const dealFlagList: boolean[] = [];
  const dealBundleCountList: string[] = [];
  const productPrice: string[] = [];
  const productCorePriceList: string[] = [];
  const badgesInfo: string[] = [];
  const sthSpecialOrderList: boolean[] = [];
  const originalPartTypeIdList: string[] = [];
  const productRepositoryIdList: string[] = [];
  const partGroupId: string[] = [];
  const partTerminologyId: string[] = [];
  const productFitmentCheckMessage: string[] = [];
  const fulfillmentAvailabilityList: string[] = [];
  let nddEligibleProductsCountV2 = 0;
  const clearanceList: string[] = [];
  const productRecommendationTypeList: string[] = [];
  const productPart = shelfCatLabel || '';
  const sisterStoreAvailableList: string[] = [];
  const productDealMessage: string[] = [];
  const productFulfillmentOptionSeenByCustomer: string[] = [];
  const partsAnalytics = () => {
    if (parts && parts.length > 0) {
      parts.forEach((part) => {
        if (part) {
          originalPartTypeIdList.push(`${part.originalPartTypeId}`);
          partGroupId.push(`${part.partGroupId ?? ''}`);
          const fulfillmentOptions =
            part.skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions ?? [];
          const partHasDeals = (part.skuPricingAndAvailability?.dealsAvailableQty ?? 0) > 0;
          const [bopusOption, onlineOption, sameDayOption] = determineOptionsToDisplayButtons(
            part.fulfillmentOptions as FulfillmentGroup[] | undefined,
          );
          const hasAvailableOption =
            (bopusOption?.available && (bopusOption?.availableQty ?? 0) > 0) ||
            (onlineOption?.available && (onlineOption?.availableQty ?? 0) > 0) ||
            (sameDayOption?.available && (sameDayOption?.availableQty ?? 0) > 0);
          const hasDiscountedPrice = !!part.skuPricingAndAvailability?.discountedPrice;
          const isMXLocale = currentProps.locale === 'es-MX';

          const dealsAvailable =
            partHasDeals &&
            hasAvailableOption &&
            currentProps.dealBadgeEnabled &&
            currentProps.storeSelected &&
            (isMXLocale ? !hasDiscountedPrice : true);

          if (dealsAvailable) {
            const discountDisplay = getDiscountDisplay(part);

            productDealMessage.push(discountDisplay);
          } else {
            productDealMessage.push('');
          }

          productRepositoryIdList.push(part.skuPricingAndAvailability?.productId ?? '');
          partTerminologyId.push(part?.productId ?? '');
          productIdList.push(part.skuNumber?.toString() ?? '');
          productPartNumberList.push(part.partNumber ?? '');
          productBrandList.push(part.brand ?? '');
          bopusList.push(part.skuPricingAndAvailability?.storePickupAvailable ?? false);
          const sisterStoreAvailable = fulfillmentOptions.filter((option) => {
            return option.sisterStoreAvailable;
          });
          if (fulfillmentOptions != null) {
            fulfillmentAvailabilityList.push(
              // @ts-expect-error fix type
              createItemFulfillmentAvailabilityString(fulfillmentOptions),
            );
            nddEligibleProductsCountV2 += fulfillmentOptions.reduce((nddCount, option) => {
              if (option.fulfillmentTypeId === 107) {
                nddCount++;
              }
              return nddCount;
            }, 0);
          }

          const [bopus, sth, ndd] = determineOptionsToDisplayButtons(
            part.fulfillmentOptions as FulfillmentGroup[] | undefined,
          );
          const optionsShown = [bopus, sth, ndd]
            .filter((option) => option?.available)
            .map((option) => String(option?.fulfillmentTypeIdOriginal))
            .join(':');
          productFulfillmentOptionSeenByCustomer.push(optionsShown);
          sthList.push(part.skuPricingAndAvailability?.shipToHomeAvailable ?? false);
          nddEligibleList.push(part.aZBadgesFlagVO?.eligibleForNextDay ?? false);
          dealFlagList.push(part.skuPricingAndAvailability?.dealAvailable ?? false);
          dealBundleCountList.push(
            dealsAvailable && part.skuPricingAndAvailability?.dealsAvailableQty
              ? part.skuPricingAndAvailability.dealsAvailableQty.toString()
              : '',
          );
          productPrice.push(part.skuPricingAndAvailability?.retailPrice?.toString() ?? '');
          productCorePriceList.push(part.skuPricingAndAvailability?.corePrice?.toString() ?? '');
          clearanceList.push(
            part.skuPricingAndAvailability?.skuAvailabilityInfo.clearanceItem ? '1' : '0',
          );
          const specialOrder = part.skuPricingAndAvailability?.specialOrder ?? false;
          const shipToHomeAvailable = part.skuPricingAndAvailability?.shipToHomeAvailable ?? false;
          sthSpecialOrderList.push(specialOrder && shipToHomeAvailable);
          const isBonusRewardsBadge = isBonusRewardsBadgeShown(
            part.bonusRewardEligible,
            part.bonusRewardAmount,
            part.bonusRewardStartDate,
            part.bonusRewardEndDate,
            isBonusRewardsBadgeEnabled,
            isUS,
          );
          const isBestSellerBadge = isBestSellerBadgeShown(part, acesEnabled, isShelfPage);

          if (isBonusRewardsBadge) {
            badgesInfo.push(badges.bonusRewards);
          } else if (isBestSellerBadge) {
            badgesInfo.push(badges.bestSeller);
          } else {
            badgesInfo.push('');
          }
          if (part.isSponsoredCard) {
            productRecommendationTypeList.push('Sponsored Product');
          } else {
            productRecommendationTypeList.push('AZ Product');
          }
          if (part.vehicleFitment === 'MAY_NOT_FIT') {
            productFitmentCheckMessage.push('may not fit your vehicle');
          } else if (!part.vehicleFit && !part.vehicleSpecific) {
            productFitmentCheckMessage.push('');
          } else {
            if (part.vehicleFitment == 'NEEDS_MORE_INFO') {
              productFitmentCheckMessage.push('Need More Info');
            }
            if (part.vehicleFitment === 'DOES_NOT_FIT') {
              productFitmentCheckMessage.push("doesn't fit your vehicle");
            }
            if (part.vehicleFitment === 'FITS') {
              productFitmentCheckMessage.push(
                part.vehicleFitmentLabel === 'UNIVERSAL' || part.recordType === 'non-app'
                  ? ''
                  : 'fits your vehicle',
              );
            }
            if (part.vehicleFitment === 'NEEDS_BASE_VEHICLE') {
              productFitmentCheckMessage.push('check if this fits your vehicle');
            }
          }
          if (currentProps.storeSelected && sisterStoreAvailable.length) {
            sisterStoreAvailableList.push('1');
          } else {
            sisterStoreAvailableList.push('');
          }
        }
      });
    }
  };

  partsAnalytics();
  const productNotAvailableList = bopusList.map<boolean>((list, index) => {
    return !list && !sthList[index];
  });
  const shelfPageBodyData = shelfPageBody?.find(
    (item) => item['@type'] === 'ShelfPageBody_ResultsList',
  ) as ShelfPageBodyItemResultsList | undefined;

  const resultsPerPage =
    (shelfPageBodyData?.recsPerPage?.toString() || brandPageBody?.recordsPerPage.toString()) ?? '';
  const searchResultCount =
    (shelfPageBodyData?.totalNumRecs?.toString() || brandPageBody?.total?.toString()) ?? '';
  const firstRecNum = shelfPageBodyData?.firstRecNum ?? brandPageBody?.firstRecNum ?? '';
  const lastRecNum = shelfPageBodyData?.lastRecNum ?? brandPageBody?.lastRecNum ?? '';
  const displayView = currentProps.isList ?? true;
  const displayViewType = displayView ? displayViews.list : displayViews.grid;
  const nullResultsDisplayType = getNullResultsDisplayViewType(parts, productFilterData);
  const nullCartridgeData = selectedNullCartridge
    ? {
        nullCartridgeSelected: selectedNullCartridge,
      }
    : {};
  const pageDepth = `${firstRecNum} to ${lastRecNum} of ${searchResultCount}`;
  delete root.utag_data?.rewardPoints;
  // TODO: probably not needed, but due to type inconsistency in the data layer, we need to check for the existence of the property
  const unifiedProductFilterData = !productFilterData
    ? null
    : 'refinementList' in productFilterData
      ? productFilterData
      : 'contents' in productFilterData
        ? productFilterData.contents[0]
        : null;

  return {
    categoryLevel1,
    categoryLevel2,
    categoryLevel3,
    productFilterSelection,
    productPart,
    productIdList,
    productPartNumberList,
    productBrandList,
    nddEligibleProductsCount: nddEligibleProductsCountV2,
    dealFlagList,
    dealBundleCountList,
    productPrice,
    productCorePriceList,
    resultsPerPage,
    searchResultCount,
    displayViewType: `${displayViewType}${nullResultsDisplayType}`,
    pageDepth,
    badgesInfo,
    sthSpecialOrderList,
    ...(acesEnabled
      ? {
          partGroupId,
          partTerminologyId,
        }
      : {
          originalPartTypeIdList,
          productRepositoryIdList,
        }),
    fulfillmentAvailabilityList,
    bopusList,
    sthList,
    nddEligibleList,
    productNotAvailableList,
    productRecommendationTypeList,
    productFitmentCheckMessage,
    sisterStoreAvailableList,
    productDealMessage,
    ...nullCartridgeData,
    ...getProductFilterAnalytics(unifiedProductFilterData),
    clearanceList,
    productFulfillmentOptionSeenByCustomer,
  };
};

export const getProductFilterAnalytics = (
  productFilterData?:
    | {
        refinementList: Array<{ label: string }>;
      }
    | {
        '@type': string;
        refinementList: Array<{
          label: string;
        }>;
      }
    | null,
) => {
  const productFilterRefinements = productFilterData?.refinementList;

  if (Array.isArray(productFilterRefinements) && productFilterRefinements.length) {
    const nullCartridgeRecommendations = productFilterRefinements.reduce(
      (result: string[], currentValue) => {
        result.push(`${currentValue.label}`);
        return result;
      },
      [],
    );
    return {
      nullCartridgeRecommendations: nullCartridgeRecommendations.join('|'),
    };
  }

  return {};
};

export const getNullResultsDisplayViewType = (
  parts: ShelfPart[] | undefined,
  productFilterData?: {
    '@type'?: string;
    refinementList?: Array<{ label: string }>;
  } | null,
): string => {
  const productFilterDataExists = productFilterData?.refinementList?.length ?? 0 > 0;
  const zeroResults = !parts?.length;

  if (zeroResults && !productFilterDataExists) {
    return displayViews.nullResults;
  } else if (zeroResults && productFilterDataExists) {
    return displayViews.nullResultsCartridge;
  }

  return '';
};

export const getSelectedSortingOptionLabel = (
  queryParameter?: string | null,
  sortingOptions?: SortingOption[] | null,
  defaultValue?: string | null,
): string => {
  const defaultLabel = defaultValue || '';

  if (Array.isArray(sortingOptions) && sortingOptions.length) {
    const selectedSortingOption = sortingOptions.find(
      (option) => option.parameter === queryParameter,
    );
    return selectedSortingOption ? selectedSortingOption.name : defaultLabel;
  }

  return defaultLabel;
};

export const getDiscountDisplay = (part: ShelfPart) => {
  const dealAvailable = part.skuPricingAndAvailability?.dealAvailable ?? false;
  const retailPrice = parseFloat(String(part.skuPricingAndAvailability?.wasRetailPrice ?? 0));
  const discountedPrice = parseFloat(String(part.skuPricingAndAvailability?.retailPrice ?? 0));
  const savingsPercent = Math.abs(Math.ceil(100 - (discountedPrice / retailPrice) * 100));
  const discountAmount = Math.abs(
    parseFloat(String(retailPrice)) - parseFloat(String(discountedPrice)),
  );
  const dealLeadingMessage =
    part.skuPricingAndAvailability?.dealConfigurableLabel || 'See Available Deals';
  if (dealAvailable && retailPrice > discountedPrice) {
    return `${dealLeadingMessage} - ${
      discountAmount > savingsPercent ? `$${discountAmount}` : `${savingsPercent}%`
    }`;
  } else if (dealAvailable) {
    return part.skuPricingAndAvailability?.dealConfigurableLabel || 'See Available Deals';
  } else if (retailPrice > discountedPrice) {
    return discountAmount > savingsPercent ? `$${discountAmount}` : `${savingsPercent}%`;
  }
  return '';
};
